/**
 * Colonne / filtre « Commercial » NR2J.
 * Toute vue multi-commerciaux (Super Admin, Administratif, Manager) affiche le propriétaire ;
 * le commercial (vue SELF) ne voit que ses dossiers → colonne masquée.
 */

export function nr2jViewerKind(session) {
  if (!session) return 'self';
  if (session.role === 'SUPER_ADMIN') return 'admin';
  if (session.role !== 'NR2J_STAFF') return 'self';
  if (session.platformProfile === 'ADMINISTRATIF_NR2J') return 'admin';
  if (session.platformProfile === 'MANAGER_NR2J') return 'manager';
  return 'self';
}

export function nr2jShowsOwnerColumn(kind) {
  return kind === 'admin' || kind === 'manager';
}

/** Fiche collaborateur (/nr2j/mon-equipe/:userId) : Manager et Super Admin uniquement. */
export function nr2jCanOpenMember(session) {
  if (!session) return false;
  if (session.role === 'SUPER_ADMIN') return true;
  return session.role === 'NR2J_STAFF' && session.platformProfile === 'MANAGER_NR2J';
}

export function nr2jOwnerName(user) {
  if (!user) return '';
  const full = `${user.prenom || ''} ${user.nom || ''}`.trim();
  return full || user.email || '';
}

export function nr2jOwnerInitials(user) {
  if (!user) return '';
  const prenom = String(user.prenom || '').trim();
  const nom = String(user.nom || '').trim();
  if (prenom && nom) return `${prenom[0]}${nom[0]}`.toUpperCase();
  if (prenom) return prenom.slice(0, 2).toUpperCase();
  if (nom) return nom.slice(0, 2).toUpperCase();
  return String(user.email || '?').slice(0, 2).toUpperCase();
}

/** Propriétaire d'une ligne liste (organisation, devis, tâche). */
export function nr2jRowOwner(row) {
  return row?.ownerUser || row?.organization?.ownerUser || null;
}

/**
 * Valeur filtre → query API.
 * '' tous | 'me' | 'user:<id>' | 'none' non attribués | 'nomanager' | 'manager:<id>'
 */
export function nr2jOwnerFilterParams(value) {
  const v = String(value || '');
  if (!v) return {};
  if (v === 'me') return { ownerUserId: 'me' };
  if (v === 'none') return { ownerUserId: 'none' };
  if (v === 'nomanager') return { managerUserId: 'none' };
  if (v.startsWith('user:')) return { ownerUserId: v.slice(5) };
  if (v.startsWith('manager:')) return { managerUserId: v.slice(8) };
  return {};
}

function byName(a, b) {
  return nr2jOwnerName(a).localeCompare(nr2jOwnerName(b), 'fr');
}

/**
 * Options du filtre Commercial.
 * @param {'admin'|'manager'|'self'} kind
 * @param {{ members?: object[], viewerId?: string|null, extraOwners?: object[] }} input
 * @returns {{ value: string, label: string, group?: string }[]}
 */
export function buildNr2jOwnerFilterOptions(kind, { members = [], viewerId = null, extraOwners = [] } = {}) {
  if (kind === 'manager') {
    const reports = members.filter((m) => m && m.id && m.id !== viewerId).sort(byName);
    return [
      { value: '', label: 'Toute mon équipe' },
      { value: 'me', label: 'Moi' },
      ...reports.map((m) => ({
        value: `user:${m.id}`,
        label: nr2jOwnerName(m),
        group: 'Mes collaborateurs',
      })),
    ];
  }
  if (kind !== 'admin') return [];

  const people = new Map();
  for (const m of members) {
    if (m?.id && (m.role === 'NR2J_STAFF' || m.role === 'SUPER_ADMIN' || !m.role)) people.set(m.id, m);
  }
  for (const o of extraOwners) {
    if (o?.id && !people.has(o.id)) people.set(o.id, o);
    if (o?.manager?.id && !people.has(o.manager.id)) people.set(o.manager.id, o.manager);
  }
  const all = [...people.values()];
  const managerIds = new Set(
    all
      .filter((p) => p.platformProfile === 'MANAGER_NR2J')
      .map((p) => p.id),
  );
  for (const p of all) {
    if (p.managerUserId) managerIds.add(p.managerUserId);
  }
  const managers = all.filter((p) => managerIds.has(p.id)).sort(byName);
  const individuals = all
    .filter((p) => p.id !== viewerId)
    .sort(byName);

  return [
    { value: '', label: 'Tous les commerciaux' },
    { value: 'me', label: 'Moi' },
    { value: 'none', label: 'Non attribués' },
    { value: 'nomanager', label: 'Sans manager' },
    ...managers.map((m) => ({
      value: `manager:${m.id}`,
      label: `Équipe de ${nr2jOwnerName(m)}`,
      group: 'Par manager',
    })),
    ...individuals.map((p) => ({
      value: `user:${p.id}`,
      label: nr2jOwnerName(p),
      group: 'Par commercial',
    })),
  ];
}
